import React, { ReactNode, RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import SyntaxHighlighter from "react-syntax-highlighter/dist/esm/prism-light";
import tsx from "react-syntax-highlighter/dist/esm/languages/prism/tsx";
import { cn, FancyContextProvider, InlineText, Spinner } from "../../lib/src";
import { ThemeProvider } from "../../lab/src";
import { FancySyntaxHighlighterStyle } from "./fancy-highlight-style";
import { ContextControls, ExampleContextProps } from "./docs-context-controls";
import * as scss from "./example-render.scss";

SyntaxHighlighter.registerLanguage("tsx", tsx);

type ExampleRenderProps = {
	children: ReactNode;
	code: string;
	props?: unknown;
	showCodeInitial?: boolean;
	customBar?: ReactNode;
};

export function ExampleRender(props: ExampleRenderProps): JSX.Element {
	const [showCode, codeToggle] = useToggle("Code", props.showCodeInitial);
	const [showProps, propsToggle] = useToggle("Props");
	const [showHtml, htmlToggle] = useToggle("HTML output");

	const ref = useRef<HTMLDivElement>(null);
	const html = useRefToHtml(ref, showHtml);

	const [exampleContext, setExampleContext] = useState<ExampleContextProps>({
		formattingLang: "en-US",
		lang: "en-US",
		staticMode: false,
		theme: "default",
	});

	return (
		<div className={scss.container}>
			<div tabIndex={-1} className={cn(scss.renderWrapper)} ref={ref}>
				<FancyContextProvider {...exampleContext}>
					<ThemeProvider theme={exampleContext.theme}>{props.children}</ThemeProvider>
				</FancyContextProvider>
			</div>

			{props.customBar && <div className={cn(scss.miniBar, scss.custom)}>{props.customBar}</div>}
			<div className={scss.miniBar}>
				{codeToggle}
				{props.props && propsToggle}
				{htmlToggle}
				<div className={scss.contextControls}>
					{exampleContext.lang !== "en-US" && (
						<InlineText>
							Language: <InlineText emphasis="strong">{exampleContext.lang}</InlineText>
						</InlineText>
					)}
					{exampleContext.formattingLang !== "en-US" && (
						<InlineText>
							Format: <InlineText emphasis="strong">{exampleContext.formattingLang}</InlineText>
						</InlineText>
					)}
					{exampleContext.staticMode === true && (
						<InlineText>
							Static Mode: <InlineText emphasis="strong">On</InlineText>
						</InlineText>
					)}
					{exampleContext.theme !== "default" && (
						<InlineText>
							Theme: <InlineText emphasis="strong">{exampleContext.theme}</InlineText>
						</InlineText>
					)}
					<ContextControls value={exampleContext} onChange={setExampleContext} />
				</div>
			</div>
			<div className={scss.extraContainer}>
				<CodeShowcase shown={showCode} code={props.code} />
				{props.props && <CodeShowcase shown={showProps} code={propsToNiceString(props.props)} />}
				<CodeShowcase shown={showHtml} code={html} />
			</div>
		</div>
	);
}

function CodeShowcase(props: { shown: boolean; code: string | null }) {
	const { shown, code } = props;
	const [mount, setMount] = useState(shown);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (shown && !mount) {
			setMount(true);
		}
		if (!shown && mount) {
			const t = setTimeout(() => setMount(false), 400);
			return () => clearTimeout(t);
		}
	}, [shown]);

	const loading = code === null;

	return (
		<div ref={ref} className={cn(scss.extra, shown && scss.shown, loading && scss.loading)}>
			<SyntaxHighlighter language="tsx" style={FancySyntaxHighlighterStyle} PreTag="div">
				{mount || shown ? code ?? "" : ""}
			</SyntaxHighlighter>
			<div className={cn(scss.spinner, loading && scss.loading)}>
				<Spinner variant="light" size="large" />
			</div>
		</div>
	);
}

function useToggle(name: string, initialValue?: boolean) {
	const [v, set] = useState(initialValue ?? false);
	const toggle = (
		<label className={scss.toggle}>
			<input type="checkbox" checked={v} onChange={() => set((s) => !s)} />
			{name}
		</label>
	);
	return [v, toggle] as const;
}

setTimeout(() => {
	import("prettier/standalone");
	import("prettier/parser-html");
}, 3000);

function useRefToHtml<T extends HTMLElement>(ref: RefObject<T>, shown: boolean) {
	const [html, setHtml] = useState<string | null>(null);
	useLayoutEffect(() => {
		(async () => {
			if (ref.current && shown) {
				const [prettier, htmlParser] = await Promise.all([
					import("prettier/standalone"),
					import("prettier/parser-html"),
				]);
				const formatted = prettier.format(ref.current.innerHTML, {
					parser: "html",
					plugins: [htmlParser],
					htmlWhitespaceSensitivity: "ignore",
					useTabs: true,
				});
				setHtml(formatted);
			}
		})();
	});
	return html;
}

function propsToNiceString(obj: unknown) {
	const str = JSON.stringify(obj, (k, v) => (typeof v == "object" && !!k ? "object" : v), "\t"); //stringify, but skip objects for now since ReactElements are recursive
	return str.replace(/"([a-zA-Z]+)":/g, "$1:"); //remove quotes from props that aren't needed
}
